import { DOCUMENT } from '@angular/common';
import { Injectable, ModuleWithProviders, NgModule, inject } from '@angular/core';

export interface CookieOptions {
  domain?: string;
  path?: string;
  expires?: Date | string | number;
  secure?: boolean;
  sameSite?: 'Lax' | 'Strict' | 'None' | string;
}

@Injectable({ providedIn: 'root' })
export class CookieService {
  private document = inject<Document>(DOCUMENT);


  get(name: string): string {
    const safeName = name.replace(/[-[\]/{}()*+?.\\^$|]/g, '\\$&');
    const match = this.document.cookie.match(new RegExp(`(?:^|; )${safeName}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : '';
  }

  put(name: string, value: string, options: CookieOptions = {}): void {
    const cookieParts = [`${name}=${encodeURIComponent(value ?? '')}`];
    cookieParts.push(`path=${options.path || '/'}`);

    if (options.domain) {
      cookieParts.push(`domain=${options.domain}`);
    }

    if (options.expires !== undefined) {
      const expires = this.parseExpires(options.expires);
      if (expires) {
        cookieParts.push(`expires=${expires.toUTCString()}`);
      }
    }

    if (options.sameSite) {
      cookieParts.push(`SameSite=${options.sameSite}`);
    }

    if (options.secure) {
      cookieParts.push('Secure');
    }

    this.document.cookie = cookieParts.join('; ');
  }

  removeAll(options: CookieOptions = {}): void {
    const cookies = (this.document.cookie || '').split(';');
    for (const cookie of cookies) {
      const [rawName] = cookie.split('=');
      const name = rawName?.trim();
      if (!name) {
        continue;
      }

      this.put(name, '', { ...options, expires: new Date(0) });
    }
  }

  private parseExpires(value: Date | string | number): Date | null {
    if (value instanceof Date) {
      return value;
    }

    if (typeof value === 'number') {
      const date = new Date();
      date.setDate(date.getDate() + value);
      return date;
    }

    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
}

@NgModule()
export class CookieModule {
  static forRoot(): ModuleWithProviders<CookieModule> {
    return {
      ngModule: CookieModule,
      providers: [CookieService],
    };
  }
}
